// Physics → Section 11: Electricity and Magnetism (KS3, sourced from
// the Grade9Ready science PDF). 7 topics as specified by the user.
import authored from './authoredTopics.json';

const AUTHORED_KEY = 'physics/electricity-and-magnetism';
const AUTHORED = (authored && authored[AUTHORED_KEY]) || [];
const AUTHORED_BY_ID = Object.fromEntries(AUTHORED.map((t) => [t.id, t]));

// =====================================================================
// 11.1  ELECTRICAL CIRCUITS
// =====================================================================
const CIRCUITS_TOPIC = {
  id: 'phys-11.1',
  title: 'Electrical Circuits',
  slug: 'electrical-circuits',
  pageRef: 'Section 11 · Topic 1',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What is a circuit?',
        body:
          'An electrical circuit is a complete loop of conductors (usually metal wires) connecting a power source (battery or mains) to one or more components (bulbs, motors, buzzers). Electric current flows around the circuit only when the loop is COMPLETE. Break the loop anywhere and the current stops — that\u2019s how switches work.',
        check: {
          question: 'What stops a circuit from working?',
          options: ['Adding wires', 'Breaking the loop anywhere', 'Adding a battery', 'Closing a switch'],
          answer: 'Breaking the loop anywhere',
          explainer: 'Current needs a complete loop. Any gap = no flow.',
        },
      },
      {
        heading: 'Circuit symbols',
        body:
          'Engineers use standard symbols to draw circuits as diagrams. Cell = a single battery (long line + short line). Battery = two or more cells. Bulb = circle with a cross. Switch = a hinged line. Wire = a plain straight line. Voltmeter = circle with V; ammeter = circle with A. These symbols mean the same in every country, so engineers worldwide can read each other\u2019s diagrams.',
        check: {
          question: 'A circle with a "V" inside represents…',
          options: ['Battery', 'Voltmeter', 'Bulb', 'Switch'],
          answer: 'Voltmeter',
          explainer: 'V = voltage; voltmeters measure potential difference.',
        },
      },
      {
        heading: 'Conductors and insulators',
        body:
          'Conductors let current flow easily — most metals (copper, aluminium, gold), graphite, and salty water. Insulators don\u2019t — plastic, rubber, glass, dry wood, dry air. Wires are made of copper (great conductor) coated in plastic (insulator) — keeping electricity in the wire and you safe outside it.',
        check: {
          question: 'Why are wires coated in plastic?',
          options: ['Plastic conducts', 'Plastic insulates and stops shocks', 'Decoration', 'Stop them rusting'],
          answer: 'Plastic insulates and stops shocks',
          explainer: 'Plastic is an electrical insulator — current stays in the copper.',
        },
      },
    ],
    keyPoints: [
      'Current flows only in a COMPLETE circuit',
      'Standard symbols: cell, battery, bulb, switch, wire, V, A',
      'Conductors: most metals, graphite',
      'Insulators: plastic, rubber, glass, dry wood',
      'Wires: copper inside, plastic outside',
    ],
    workedExamples: [
      {
        problem: 'Sketch a simple circuit with a battery, switch and bulb in series.',
        steps: [
          { step: 1, instruction: 'Battery', working: 'Two long-short pairs (cell symbols)' },
          { step: 2, instruction: 'Wire', working: 'Straight line to switch' },
          { step: 3, instruction: 'Switch', working: 'Hinged line — open or closed' },
          { step: 4, instruction: 'Wire to bulb', working: 'Straight line; bulb = circle with cross' },
          { step: 5, instruction: 'Wire back to battery', working: 'Closes the loop' },
        ],
        answer: 'A complete loop: battery → switch → bulb → back to battery.',
        commonMistake: 'Drawing wires that don\u2019t make a complete loop. Always check the line returns to the battery.',
      },
    ],
    whyItWorks:
      'Circuit diagrams are the universal language of electronics. Once you know the symbols, you can read any wiring schematic — for a phone, a power station, or your home\u2019s mains.',
  },
  glossary: [
    { term: 'Circuit', definition: 'A complete loop of conductors connecting a power source to components.' },
    { term: 'Conductor', definition: 'A material that lets current flow through it easily (e.g. copper).' },
    { term: 'Insulator', definition: 'A material that does NOT let current flow (e.g. plastic).' },
    { term: 'Cell', definition: 'A single source of voltage; combine to make a battery.' },
  ],
  examQuestion: {
    prompt:
      '(a) State the difference between a conductor and an insulator. (b) Give an example of each. (c) Explain why a circuit must form a complete loop. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Conductor lets current flow; insulator does not',
      'Conductor: copper (or any metal); insulator: plastic (or rubber/glass)',
      'Current = flow of charge',
      'Charge can only flow if it has a complete path back to the cell',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-11.1.w1', type: 'choice', question: 'Which is a conductor?', options: ['Plastic', 'Wood', 'Copper', 'Glass'], answer: 'Copper', hint1: 'Metal', hint2: 'Wires made of it', hint3: 'Reddish brown' },
      { id: 'phys-11.1.w2', type: 'choice', question: 'A complete circuit means…', options: ['Lots of wires', 'A loop with no break', 'Many switches', 'High voltage'], answer: 'A loop with no break', hint1: 'Closed loop', hint2: 'Path back to source', hint3: 'Linked to flow' },
      { id: 'phys-11.1.w3', type: 'text', question: 'Symbol "V" inside a circle = ___ .', answer: 'voltmeter', hint1: '9 letters', hint2: 'Measures p.d.', hint3: 'Starts with v' },
    ],
    level1: [
      { id: 'phys-11.1.1', type: 'text', question: 'A wire is coated in ___ to insulate it.', answer: 'plastic', hint1: '7 letters', hint2: 'Polymer', hint3: 'Starts with p' },
      { id: 'phys-11.1.2', type: 'choice', question: 'Two or more cells together = a…', options: ['Voltmeter', 'Battery', 'Bulb', 'Wire'], answer: 'Battery', hint1: 'Multiple cells', hint2: '7 letters', hint3: 'Power source' },
    ],
    level2: [
      { id: 'phys-11.1.3', type: 'choice', question: 'Why is graphite an unusual non-metal?', options: ['It rusts', 'It conducts electricity', 'It is shiny', 'It is heavy'], answer: 'It conducts electricity', hint1: 'Pencil leads', hint2: 'Free electrons in carbon layers', hint3: 'Exception to non-metal rule' },
      { id: 'phys-11.1.4', type: 'choice', question: 'Open switch in circuit means…', options: ['Current flows', 'No current flows', 'Current is faster', 'Battery dies'], answer: 'No current flows', hint1: 'Loop is broken', hint2: 'Gap in circuit', hint3: 'Open = off' },
    ],
    level3: [
      { id: 'phys-11.1.5', type: 'choice', question: 'In a circuit diagram, a small "A" inside a circle represents an…', options: ['Ammeter', 'Alarm', 'Acid', 'Aerial'], answer: 'Ammeter', hint1: 'A for ampere', hint2: '7 letters', hint3: 'Measures current' },
      { id: 'phys-11.1.6', type: 'text', question: 'The metal commonly used inside electrical wires is ___ .', answer: 'copper', hint1: '6 letters', hint2: 'Reddish-brown metal', hint3: 'Symbol Cu' },
    ],
  },
};

// =====================================================================
// 11.2  MEASURING CURRENT AND POTENTIAL DIFFERENCE
// =====================================================================
const MEASURING_TOPIC = {
  id: 'phys-11.2',
  title: 'Measuring Current and Potential Difference',
  slug: 'measuring-current-and-potential-difference',
  pageRef: 'Section 11 · Topic 2',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Current',
        body:
          'Electric current is the flow of electric charge — usually electrons in metal wires. Bigger current = more charge flowing per second. Current is measured in AMPERES (A), often shortened to "amps", using an AMMETER. The ammeter must be placed IN SERIES with the component (i.e. so all the charge flows through it).',
        check: {
          question: 'Current is measured in…',
          options: ['Volts', 'Watts', 'Amps', 'Joules'],
          answer: 'Amps',
          explainer: 'Amps = amperes = current. Symbol A.',
        },
      },
      {
        heading: 'Potential difference (voltage)',
        body:
          "Potential difference (often just called \"voltage\") is the energy given to each unit of charge by the cell — or used up by each unit of charge in a component. It's measured in VOLTS (V) using a VOLTMETER. The voltmeter is placed in PARALLEL with the component (i.e. across it, with one terminal on each side).",
        check: {
          question: 'A voltmeter is placed…',
          options: ['In series with a component', 'In parallel (across) the component', 'Anywhere', 'Inside the battery'],
          answer: 'In parallel (across) the component',
          explainer: 'Voltmeter ALWAYS goes across the component being measured.',
        },
      },
      {
        heading: 'Resistance',
        body:
          'Resistance opposes the flow of current. Bigger resistance → smaller current for the same voltage. Resistance is measured in OHMS (Ω). Ohm\u2019s law: V = I × R, where V is potential difference (V), I is current (A), and R is resistance (Ω). A higher-resistance component (like a thin wire or a small resistor) restricts current more.',
        check: {
          question: 'A 6 V cell drives a current of 2 A through a wire. Resistance?',
          options: ['3 Ω', '8 Ω', '12 Ω', '0.33 Ω'],
          answer: '3 Ω',
          explainer: 'R = V ÷ I = 6 ÷ 2 = 3 Ω.',
        },
      },
    ],
    keyPoints: [
      'Current = flow of charge; unit amps (A); measured by ammeter IN SERIES',
      'Potential difference = energy per unit charge; unit volts (V); measured by voltmeter IN PARALLEL',
      'Resistance opposes current; unit ohms (Ω)',
      'Ohm\u2019s law: V = I × R',
      'Higher resistance → smaller current (for the same voltage)',
    ],
    workedExamples: [
      {
        problem: 'A 12 V battery drives a current of 0.4 A through a bulb. Find the resistance of the bulb.',
        steps: [
          { step: 1, instruction: 'Apply Ohm\u2019s law', working: 'V = I × R → R = V ÷ I' },
          { step: 2, instruction: 'Substitute', working: 'R = 12 ÷ 0.4 = 30 Ω' },
        ],
        answer: '30 Ω.',
        commonMistake: 'Forgetting to make sure current is in AMPS (not mA).',
      },
    ],
    whyItWorks:
      "Voltage tells the charge how much energy it has; current tells you how MANY charges are flowing. Resistance tells you how hard the path is. Ohm's law (V = IR) ties them all together — one of the most useful equations in everyday electronics.",
  },
  glossary: [
    { term: 'Current (I)', definition: 'Flow of electric charge; unit amperes (A).' },
    { term: 'Potential difference (V)', definition: 'Energy per unit charge; unit volts (V).' },
    { term: 'Resistance (R)', definition: 'Opposition to current; unit ohms (Ω).' },
    { term: 'Ammeter', definition: 'Instrument that measures current. Connected IN SERIES.' },
    { term: 'Voltmeter', definition: 'Instrument that measures p.d. Connected IN PARALLEL.' },
    { term: 'Ohm\u2019s law', definition: 'V = I × R for many components at constant temperature.' },
  ],
  examQuestion: {
    prompt:
      '(a) State the units of current, p.d. and resistance. (b) State HOW each meter is connected in a circuit. (c) A 9 V battery drives 0.3 A through a wire. Calculate its resistance. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Current in amps (A); p.d. in volts (V); resistance in ohms (Ω)',
      'Ammeter in series; voltmeter in parallel',
      'V = I × R',
      'R = V ÷ I = 9 ÷ 0.3',
      '= 30 Ω',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-11.2.w1', type: 'text', question: 'Current is measured in ___ .', answer: 'amps', hint1: '4 letters', hint2: 'Symbol A', hint3: 'Plural' },
      { id: 'phys-11.2.w2', type: 'choice', question: 'Voltmeter is connected…', options: ['In series', 'In parallel (across component)', 'Inside battery', 'Anywhere'], answer: 'In parallel (across component)', hint1: 'Across both ends', hint2: 'Two prongs', hint3: 'Side-by-side' },
      { id: 'phys-11.2.w3', type: 'number', question: 'V = 6 V, I = 2 A. Resistance (Ω)?', answer: '3', hint1: 'V ÷ I', hint2: 'Single digit', hint3: 'Three' },
    ],
    level1: [
      { id: 'phys-11.2.1', type: 'choice', question: 'Ohm\u2019s law: V = ?', options: ['I + R', 'I − R', 'I × R', 'I ÷ R'], answer: 'I × R', hint1: 'Multiplication', hint2: 'V = IR', hint3: 'Common form' },
      { id: 'phys-11.2.2', type: 'text', question: 'Resistance is measured in ___ .', answer: 'ohms', hint1: '4 letters', hint2: 'Symbol Ω', hint3: 'Plural' },
    ],
    level2: [
      { id: 'phys-11.2.3', type: 'number', question: 'V = 24 V, R = 8 Ω. Current (A)?', answer: '3', hint1: '24 ÷ 8', hint2: 'Single digit', hint3: 'Three' },
      { id: 'phys-11.2.4', type: 'choice', question: 'Higher resistance → ___ current (same voltage).', options: ['Larger', 'Smaller', 'Same', 'Reversed'], answer: 'Smaller', hint1: 'Inverse', hint2: 'I = V/R', hint3: 'Less flow' },
    ],
    level3: [
      { id: 'phys-11.2.5', type: 'number', question: 'Bulb resistance 60 Ω with 12 V battery. Current (A)?', answer: '0.2', hint1: 'V ÷ R', hint2: '12 ÷ 60', hint3: 'One decimal' },
      { id: 'phys-11.2.6', type: 'text', question: 'A device that measures current is an ___ .', answer: 'ammeter', hint1: '7 letters', hint2: 'In series', hint3: 'Symbol A in circle' },
    ],
  },
};

// =====================================================================
// 11.3  SERIES CIRCUITS
// =====================================================================
const SERIES_TOPIC = {
  id: 'phys-11.3',
  title: 'Series Circuits',
  slug: 'series-circuits',
  pageRef: 'Section 11 · Topic 3',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'One path for current',
        body:
          'In a SERIES circuit, components are connected in a single loop, one after the other. The same current flows through every component (because there\u2019s only one path). The total resistance is the SUM of all the individual resistances. The cell\u2019s voltage is shared (split) between the components — the more resistance a component has, the bigger its share.',
        check: {
          question: 'In a series circuit with two bulbs, the current through bulb 1 is the SAME as the current through…',
          options: ['Half bulb 2', 'Bulb 2', 'The wire only', 'No current at all'],
          answer: 'Bulb 2',
          explainer: 'One path = one current value through every component.',
        },
      },
      {
        heading: 'Disadvantages',
        body:
          'Christmas tree fairy lights used to be in series. The big problem: if ONE bulb breaks, the whole loop is broken — every light goes out. Adding more bulbs in series also makes each one DIMMER, because the same battery voltage has to spread further. That\u2019s why most household appliances aren\u2019t in series.',
        check: {
          question: 'In a series circuit of 3 bulbs, what happens if one bulb breaks?',
          options: ['Other 2 stay on', 'Only the broken bulb goes out', 'All 3 go out', 'They get brighter'],
          answer: 'All 3 go out',
          explainer: 'Loop is broken → no current anywhere.',
        },
      },
      {
        heading: 'Working with series circuits',
        body:
          'Total resistance = R₁ + R₂ + R₃ + …  Total voltage from cell = sum of p.d. across each component (V_total = V₁ + V₂ + V₃ + …). To find current: divide total voltage by total resistance. Each component\u2019s share of voltage = current × that component\u2019s resistance.',
        check: {
          question: 'Two resistors in series: 4 Ω and 6 Ω. Total resistance?',
          options: ['2.4 Ω', '5 Ω', '10 Ω', '24 Ω'],
          answer: '10 Ω',
          explainer: 'Just add: 4 + 6 = 10 Ω.',
        },
      },
    ],
    keyPoints: [
      'Series = one loop, one path for current',
      'Same current everywhere',
      'Total resistance = sum of all resistances',
      'Voltage shared between components',
      'Break one → all stop working',
    ],
    workedExamples: [
      {
        problem: 'A 12 V cell drives current through two resistors in series: 4 Ω and 8 Ω. Find the total resistance, current, and voltage across each resistor.',
        steps: [
          { step: 1, instruction: 'Total resistance', working: '4 + 8 = 12 Ω' },
          { step: 2, instruction: 'Current', working: 'I = V ÷ R = 12 ÷ 12 = 1 A' },
          { step: 3, instruction: 'V across 4 Ω', working: 'V = I × R = 1 × 4 = 4 V' },
          { step: 4, instruction: 'V across 8 Ω', working: '1 × 8 = 8 V' },
          { step: 5, instruction: 'Check', working: '4 + 8 = 12 V ✓' },
        ],
        answer: 'R_total = 12 Ω; I = 1 A; V₁ = 4 V; V₂ = 8 V.',
        commonMistake: 'Forgetting to check that the component voltages add up to the supply voltage.',
      },
    ],
    whyItWorks:
      'In series, every electron must pass through every component, so the same number per second passes through each — same current. Each component takes a "share" of the energy, and the shares add to the cell\u2019s total — that\u2019s why component voltages add up to the cell voltage.',
  },
  glossary: [
    { term: 'Series circuit', definition: 'Components connected in a single loop with one path for current.' },
    { term: 'Sum of resistances', definition: 'For series: R_total = R₁ + R₂ + R₃ + …' },
  ],
  examQuestion: {
    prompt:
      'Three bulbs (each 4 Ω) are connected in series with a 6 V battery. (a) Calculate the total resistance. (b) Calculate the current. (c) State what happens to the current if one bulb is removed. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Total R = 4 + 4 + 4 = 12 Ω',
      'I = V ÷ R = 6 ÷ 12',
      '= 0.5 A',
      'Removing one bulb breaks the loop',
      'Current stops everywhere (all bulbs go out)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-11.3.w1', type: 'choice', question: 'In a series circuit, current is…', options: ['Same throughout', 'Bigger near the battery', 'Smaller far away', 'Random'], answer: 'Same throughout', hint1: 'One path', hint2: 'Same value at every point', hint3: 'Constant flow' },
      { id: 'phys-11.3.w2', type: 'choice', question: 'Two 5 Ω resistors in series. Total?', options: ['2.5 Ω', '5 Ω', '10 Ω', '25 Ω'], answer: '10 Ω', hint1: 'Add them', hint2: '5 + 5', hint3: 'Two digits' },
      { id: 'phys-11.3.w3', type: 'choice', question: 'If one bulb in a series chain breaks…', options: ['Only that bulb goes out', 'All bulbs go out', 'Brighter', 'Battery overloads'], answer: 'All bulbs go out', hint1: 'Loop broken', hint2: 'No path', hint3: 'Old fairy lights' },
    ],
    level1: [
      { id: 'phys-11.3.1', type: 'number', question: '3 V cell, 1 Ω + 2 Ω in series. Current (A)?', answer: '1', hint1: 'Total R = 3 Ω', hint2: '3 ÷ 3', hint3: 'Single digit' },
      { id: 'phys-11.3.2', type: 'text', question: 'In a series circuit, voltages across components ___ to the cell\u2019s p.d.', answer: 'add', hint1: '3 letters', hint2: 'Sum to total', hint3: 'Plus' },
    ],
    level2: [
      { id: 'phys-11.3.3', type: 'number', question: '12 V battery, 6 Ω + 6 Ω in series. V across one resistor?', answer: '6', hint1: 'Equal share', hint2: 'Half of 12', hint3: 'Single digit' },
      { id: 'phys-11.3.4', type: 'choice', question: 'Adding more bulbs in series makes them…', options: ['Brighter', 'Dimmer', 'Same brightness', 'Off'], answer: 'Dimmer', hint1: 'Voltage shared', hint2: 'Less p.d. each', hint3: 'Less energy per bulb' },
    ],
    level3: [
      { id: 'phys-11.3.5', type: 'number', question: '4 Ω + 8 Ω + 12 Ω in series with 24 V. Current (A)?', answer: '1', hint1: 'Total R = 24 Ω', hint2: '24 ÷ 24', hint3: 'Single digit' },
      { id: 'phys-11.3.6', type: 'text', question: 'In series, total resistance is the ___ of all resistances.', answer: 'sum', hint1: '3 letters', hint2: 'Adding', hint3: 'Total' },
    ],
  },
};

// =====================================================================
// 11.4  PARALLEL CIRCUITS
// =====================================================================
const PARALLEL_TOPIC = {
  id: 'phys-11.4',
  title: 'Parallel Circuits',
  slug: 'parallel-circuits',
  pageRef: 'Section 11 · Topic 4',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Multiple paths',
        body:
          'In a PARALLEL circuit, the components are connected on separate branches that all share the same two ends. Current splits between the branches and recombines after them. Each branch has the FULL cell voltage across it. If one branch breaks, the others STILL work.',
        check: {
          question: 'Three bulbs are wired in parallel. One is unscrewed. What happens to the other two?',
          options: ['They go out', 'They keep glowing', 'They get dimmer', 'They explode'],
          answer: 'They keep glowing',
          explainer: 'Other branches are still complete loops back to the battery.',
        },
      },
      {
        heading: 'Voltage and current',
        body:
          'Voltage across each branch is the SAME and equal to the cell voltage. Currents in branches ADD UP to the total current from the cell: I_total = I₁ + I₂ + I₃ + … A bulb on its own branch with a high resistance gets a small current; a bulb on its branch with a low resistance gets a big current.',
        check: {
          question: 'A 12 V battery drives 2 bulbs in parallel. p.d. across each bulb?',
          options: ['6 V', '12 V', '24 V', '4 V'],
          answer: '12 V',
          explainer: 'Each parallel branch has the FULL cell voltage.',
        },
      },
      {
        heading: 'Why homes use parallel',
        body:
          'Mains wiring at home uses parallel circuits. That way: every appliance gets the FULL 230 V supply (so all run at full power); turning off the kettle doesn\u2019t turn off the lights; and a broken bulb doesn\u2019t bring down the whole house. Add up the currents in each branch to get the total current from the supply — and that\u2019s what fuses are sized to handle.',
        check: {
          question: 'Why are appliances at home connected in parallel?',
          options: ['Cheaper wiring', 'Each gets full mains voltage; one fault doesn\u2019t affect others', 'They look nicer', 'Only choice available'],
          answer: 'Each gets full mains voltage; one fault doesn\u2019t affect others',
          explainer: 'Parallel = independent operation + full voltage on each device.',
        },
      },
    ],
    keyPoints: [
      'Parallel = multiple branches between same two points',
      'Same VOLTAGE across each branch',
      'Currents in branches add to total current',
      'Break one branch → others keep working',
      'Mains wiring at home uses parallel',
    ],
    workedExamples: [
      {
        problem: 'A 12 V battery is connected to two bulbs in parallel: bulb 1 = 4 Ω, bulb 2 = 6 Ω. Find current through each bulb and total current.',
        steps: [
          { step: 1, instruction: 'V on each bulb', working: '12 V (parallel = same voltage)' },
          { step: 2, instruction: 'I₁', working: '12 ÷ 4 = 3 A' },
          { step: 3, instruction: 'I₂', working: '12 ÷ 6 = 2 A' },
          { step: 4, instruction: 'Total', working: 'I_total = 3 + 2 = 5 A' },
        ],
        answer: 'I₁ = 3 A; I₂ = 2 A; total = 5 A.',
        commonMistake: 'Sharing the 12 V between bulbs (that\u2019s SERIES). In parallel, EACH bulb gets the full 12 V.',
      },
    ],
    whyItWorks:
      'Parallel circuits give independence — each branch works on its own. That\u2019s why your house has parallel wiring (no single bulb takes everything down) and why hospital backup systems are parallel (one failure doesn\u2019t kill the whole grid).',
  },
  glossary: [
    { term: 'Parallel circuit', definition: 'Components on separate branches between the same two points.' },
    { term: 'Branch', definition: 'A single path in a parallel circuit.' },
  ],
  examQuestion: {
    prompt:
      'A 6 V battery is connected to a 2 Ω bulb and a 3 Ω bulb in parallel. (a) State the p.d. across each bulb. (b) Calculate the current through each. (c) State what happens if the 2 Ω bulb breaks. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Each bulb has 6 V across it',
      'I₁ = 6 ÷ 2 = 3 A',
      'I₂ = 6 ÷ 3 = 2 A',
      'The 3 Ω bulb continues to glow',
      'Because its branch is still a complete loop with the battery',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-11.4.w1', type: 'choice', question: 'In parallel, voltage across each branch is…', options: ['Less', 'Equal to cell voltage', 'Bigger', 'Zero'], answer: 'Equal to cell voltage', hint1: 'Full p.d.', hint2: 'No sharing', hint3: 'Same on every branch' },
      { id: 'phys-11.4.w2', type: 'choice', question: 'Break one branch in parallel. Others…', options: ['Stop', 'Continue', 'Get dimmer', 'Explode'], answer: 'Continue', hint1: 'Independent paths', hint2: 'Their loop intact', hint3: 'Still complete' },
      { id: 'phys-11.4.w3', type: 'number', question: '12 V over 4 Ω in parallel branch. Current (A)?', answer: '3', hint1: '12 ÷ 4', hint2: 'Single digit', hint3: 'Three' },
    ],
    level1: [
      { id: 'phys-11.4.1', type: 'choice', question: 'Branches in parallel get…', options: ['Different voltages', 'Same voltage as cell', 'Half voltage', 'No voltage'], answer: 'Same voltage as cell', hint1: 'Equal across both ends', hint2: 'Full p.d.', hint3: 'No voltage drop' },
      { id: 'phys-11.4.2', type: 'text', question: 'Total current in a parallel circuit is the ___ of branch currents.', answer: 'sum', hint1: '3 letters', hint2: 'Adding', hint3: 'Total' },
    ],
    level2: [
      { id: 'phys-11.4.3', type: 'number', question: '6 V battery, two 6 Ω bulbs in parallel. Total current (A)?', answer: '2', hint1: '1 A through each', hint2: '1 + 1', hint3: 'Single digit' },
      { id: 'phys-11.4.4', type: 'choice', question: 'Why does adding more parallel branches DECREASE total resistance?', options: ['More paths share the current', 'Fewer paths', 'Random', 'Magic'], answer: 'More paths share the current', hint1: 'Easier flow', hint2: 'More routes', hint3: 'Less resistance overall' },
    ],
    level3: [
      { id: 'phys-11.4.5', type: 'number', question: '12 V battery, 4 Ω + 12 Ω in parallel. Current through 4 Ω (A)?', answer: '3', hint1: '12 ÷ 4', hint2: 'Single digit', hint3: 'Three' },
      { id: 'phys-11.4.6', type: 'text', question: 'Houses are wired in ___ so each appliance gets full mains voltage.', answer: 'parallel', hint1: '8 letters', hint2: 'Topic title', hint3: 'Starts with p' },
    ],
  },
};

// =====================================================================
// 11.5  STATIC ELECTRICITY
// =====================================================================
const STATIC_TOPIC = {
  id: 'phys-11.5',
  title: 'Static Electricity',
  slug: 'static-electricity',
  pageRef: 'Section 11 · Topic 5',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Charges that don\u2019t move',
        body:
          "Atoms contain positive protons in the nucleus and negative electrons orbiting around. If you rub two insulators together (e.g. a balloon on hair), some electrons can transfer from one to the other. The object that GAINED electrons becomes negatively charged; the one that LOST them becomes positively charged. The charges aren't flowing — they're STATIC.",
        check: {
          question: 'A balloon rubbed on hair gains electrons. The balloon is now…',
          options: ['Positively charged', 'Negatively charged', 'Neutral', 'Magnetised'],
          answer: 'Negatively charged',
          explainer: 'Extra electrons (negative) → negatively charged.',
        },
      },
      {
        heading: 'Like and unlike charges',
        body:
          'LIKE charges REPEL (push apart): positive-positive or negative-negative. UNLIKE charges ATTRACT: positive-negative. A charged object can also pick up small bits of paper or hair because it INDUCES the opposite charge in the nearby surface — making them attract briefly.',
        check: {
          question: 'Two negatively charged balloons placed close together…',
          options: ['Stick together', 'Repel each other', 'Become neutral', 'Glow'],
          answer: 'Repel each other',
          explainer: 'Like charges repel.',
        },
      },
      {
        heading: 'Useful and unwanted static',
        body:
          'USEFUL: photocopiers and laser printers use static charge to attract toner powder onto paper; spray-painting cars uses charged droplets to coat evenly; smoke filters charge particles to attract them out of chimney smoke. UNWANTED: clingy clothes, electric shocks from doorknobs, lightning (a giant electrostatic discharge), and the danger of sparks near fuel — that\u2019s why fuel tankers have grounding straps.',
        check: {
          question: 'Why are large fuel tanker trucks earthed (grounded) before refilling?',
          options: ['To balance the truck', 'To prevent static sparks igniting the fuel', 'To weigh it', 'To stop rust'],
          answer: 'To prevent static sparks igniting the fuel',
          explainer: 'A spark + petrol vapour = explosion. Earthing safely drains static charge.',
        },
      },
    ],
    keyPoints: [
      'Friction transfers electrons between insulators',
      'Object gaining electrons = negative; losing = positive',
      'Like charges repel; unlike attract',
      'Photocopiers + spray painting + smoke filters use static usefully',
      'Lightning + petrol-station shocks = unwanted static',
    ],
    workedExamples: [
      {
        problem: 'A plastic ruler rubbed with a cloth picks up small bits of paper. Explain why.',
        steps: [
          { step: 1, instruction: 'Rubbing transfers', working: 'Electrons move from cloth to ruler (or vice versa)' },
          { step: 2, instruction: 'Result', working: 'Ruler is now charged' },
          { step: 3, instruction: 'Effect on paper', working: 'Charged ruler INDUCES opposite charge on the paper surface' },
          { step: 4, instruction: 'Force', working: 'Opposite charges attract → paper jumps onto ruler' },
        ],
        answer: 'Friction charges the ruler; charged ruler induces opposite charge in the paper, so they attract.',
        commonMistake: 'Saying paper is "magnetic". It\u2019s charge attraction, not magnetism.',
      },
    ],
    whyItWorks:
      'Static is just charge that\u2019s stuck in place. Pile up enough charge, and you get sparks (lightning), shocks, or visible cling effects. The same effect printers use to put a perfectly even layer of black on a page.',
  },
  glossary: [
    { term: 'Static electricity', definition: 'Charge that stays in one place rather than flowing.' },
    { term: 'Electron', definition: 'A negatively charged particle that orbits the nucleus of an atom.' },
    { term: 'Like charges repel', definition: '+/+ or −/− push each other apart.' },
    { term: 'Unlike charges attract', definition: '+/− pull each other together.' },
    { term: 'Earthing (grounding)', definition: 'A wire to ground that safely drains static charge.' },
  ],
  examQuestion: {
    prompt:
      "(a) Explain how a balloon gets a charge when rubbed on hair. (b) State whether the balloon and your hair attract or repel each other afterwards. (c) Suggest TWO useful applications of static electricity. (5 marks)",
    maxMarks: 5,
    markSchemePoints: [
      'Friction causes electrons to transfer between balloon and hair',
      'Balloon gains electrons → becomes negatively charged',
      'Balloon and hair have opposite charges → attract each other',
      'Photocopier/laser printer uses charged toner',
      'Spray painting / smoke filters / inkjet printers (any 2)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-11.5.w1', type: 'choice', question: 'Like charges…', options: ['Attract', 'Repel', 'Cancel', 'Glow'], answer: 'Repel', hint1: 'Push apart', hint2: 'Same sign', hint3: '5 letters' },
      { id: 'phys-11.5.w2', type: 'choice', question: 'A negative balloon and positive hair…', options: ['Attract', 'Repel', 'Don\u2019t affect each other', 'Spark'], answer: 'Attract', hint1: 'Opposite charges', hint2: '+ and −', hint3: 'Pull together' },
      { id: 'phys-11.5.w3', type: 'text', question: 'Object that gains electrons becomes ___ charged.', answer: 'negatively', hint1: '10 letters', hint2: 'Symbol −', hint3: 'Adverb' },
    ],
    level1: [
      { id: 'phys-11.5.1', type: 'text', question: 'Friction transfers ___ between two surfaces.', answer: 'electrons', hint1: '9 letters', hint2: 'Negative particles', hint3: 'Symbol e⁻' },
      { id: 'phys-11.5.2', type: 'choice', question: 'A useful application of static is…', options: ['Lightning', 'Photocopier toner', 'Doorknob shocks', 'Cling film static'], answer: 'Photocopier toner', hint1: 'Useful tech', hint2: 'Charge attracts powder', hint3: 'Office machine' },
    ],
    level2: [
      { id: 'phys-11.5.3', type: 'choice', question: 'Why do petrol tankers have grounding straps?', options: ['Weigh truck', 'Drain static so no sparks ignite fuel', 'Stop rust', 'Mark the truck'], answer: 'Drain static so no sparks ignite fuel', hint1: 'Safety', hint2: 'Avoid spark + vapour', hint3: 'Earth the charge' },
      { id: 'phys-11.5.4', type: 'text', question: 'Lightning is a huge electrostatic ___ .', answer: 'discharge', hint1: '9 letters', hint2: 'Big spark', hint3: 'Sudden release' },
    ],
    level3: [
      { id: 'phys-11.5.5', type: 'choice', question: 'Why does a charged ruler attract small paper bits?', options: ['Magnetism', 'Induction — opposite charge appears on paper, then attraction', 'Adhesion', 'Wind'], answer: 'Induction — opposite charge appears on paper, then attraction', hint1: 'Surface induces opposite charge', hint2: 'Then attraction', hint3: 'Two-step process' },
      { id: 'phys-11.5.6', type: 'text', question: 'The wire that drains charge to the ground is called the ___ wire.', answer: 'earth', hint1: '5 letters', hint2: 'Ground name', hint3: 'Starts with e' },
    ],
  },
};

// =====================================================================
// 11.6  MAGNETS
// =====================================================================
const MAGNETS_TOPIC = {
  id: 'phys-11.6',
  title: 'Magnets',
  slug: 'magnets',
  pageRef: 'Section 11 · Topic 6',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Magnetic poles',
        body:
          'Every magnet has two POLES — a NORTH pole and a SOUTH pole. The poles are where the magnetic force is strongest. Like poles REPEL each other (N-N or S-S push apart) and unlike poles ATTRACT (N-S pull together). Magnets attract a few specific metals: iron, nickel, cobalt and steel (which contains iron) — these are MAGNETIC materials.',
        check: {
          question: 'Two north poles brought together…',
          options: ['Attract', 'Repel', 'No effect', 'Stick'],
          answer: 'Repel',
          explainer: 'Like poles repel — same as charges.',
        },
      },
      {
        heading: 'Magnetic fields',
        body:
          'Around every magnet is a MAGNETIC FIELD — a region where its force can be felt. Field lines (drawn on paper) go FROM the north pole TO the south pole outside the magnet. Field lines never cross. They\u2019re closer together where the field is STRONGER — usually right at the poles. Iron filings sprinkled near a magnet line up along the field lines, making them visible.',
        check: {
          question: 'Magnetic field lines run from…',
          options: ['South to north outside the magnet', 'North to south outside the magnet', 'Random', 'They cross each other'],
          answer: 'North to south outside the magnet',
          explainer: 'Outside the magnet: field lines go N → S.',
        },
      },
      {
        heading: 'Earth as a magnet',
        body:
          'Earth itself is a giant magnet (because of the spinning iron core). A compass needle is a tiny magnet that lines up with Earth\u2019s field — the END pointing roughly to the geographic North is by convention called the NORTH pole of the needle. Confusingly, the geographic "north" is actually a magnetic SOUTH pole — that\u2019s why the needle\u2019s north (which should be repelled by another north) ends up pointing there.',
        check: {
          question: 'Why does a compass needle point roughly North?',
          options: ['Magic', 'It aligns with Earth\u2019s magnetic field', 'Gravity', 'Wind'],
          answer: 'It aligns with Earth\u2019s magnetic field',
          explainer: 'The needle is a tiny magnet; Earth\u2019s field swings it.',
        },
      },
    ],
    keyPoints: [
      'Magnets have north and south poles',
      'Like poles repel; unlike attract',
      'Magnetic materials: iron, nickel, cobalt, steel',
      'Field lines: N → S outside the magnet, never cross',
      'Earth is a giant magnet → compasses align with its field',
    ],
    workedExamples: [
      {
        problem: 'Sketch the field lines around a bar magnet.',
        steps: [
          { step: 1, instruction: 'Label poles', working: 'Mark one end N (north), other end S (south)' },
          { step: 2, instruction: 'Draw field lines', working: 'Curves leaving N, looping around to enter S' },
          { step: 3, instruction: 'Density', working: 'Lines closer together near the poles (stronger field)' },
          { step: 4, instruction: 'Direction', working: 'Add arrows pointing N → S outside the magnet' },
        ],
        answer: 'Curved lines from N to S, denser at poles, with arrows.',
        commonMistake: 'Drawing crossing lines — field lines NEVER cross.',
      },
    ],
    whyItWorks:
      'A magnet is essentially a chunk of material in which all the tiny atomic magnets line up the same way. The field around it is the sum of all those tiny aligned fields. Spin a planet of iron, and you get the same effect on a vast scale — Earth\u2019s magnetic field.',
  },
  glossary: [
    { term: 'Pole', definition: 'A region (N or S) of a magnet where the field is strongest.' },
    { term: 'Magnetic field', definition: 'A region around a magnet where its force can be felt.' },
    { term: 'Field line', definition: 'A line showing the direction of the magnetic field at each point.' },
    { term: 'Magnetic material', definition: 'A material attracted to magnets (iron, nickel, cobalt, steel).' },
  ],
  examQuestion: {
    prompt:
      '(a) Name THREE magnetic materials. (b) State the rule for the forces between magnetic poles. (c) Explain why a compass needle points roughly North on Earth. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Iron, nickel, cobalt (or steel)',
      'Like poles repel, unlike poles attract',
      'Compass needle is a small magnet',
      'It aligns with Earth\u2019s magnetic field, which has a N-S orientation',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-11.6.w1', type: 'text', question: 'Magnets attract iron, nickel and ___ .', answer: 'cobalt', hint1: '6 letters', hint2: 'Element symbol Co', hint3: 'Starts with c' },
      { id: 'phys-11.6.w2', type: 'choice', question: 'Like poles…', options: ['Attract', 'Repel', 'Cancel', 'Glow'], answer: 'Repel', hint1: 'Push apart', hint2: '5 letters', hint3: 'Same as like charges' },
      { id: 'phys-11.6.w3', type: 'choice', question: 'Field lines go from…', options: ['S to N outside', 'N to S outside', 'Random', 'Cross over'], answer: 'N to S outside', hint1: 'Outside the magnet', hint2: 'North out', hint3: 'Standard convention' },
    ],
    level1: [
      { id: 'phys-11.6.1', type: 'text', question: 'A region around a magnet where its force acts is the magnetic ___ .', answer: 'field', hint1: '5 letters', hint2: 'Around the magnet', hint3: 'Starts with f' },
      { id: 'phys-11.6.2', type: 'choice', question: 'A north pole and south pole brought together…', options: ['Repel', 'Attract', 'Cancel', 'Spark'], answer: 'Attract', hint1: 'Unlike poles', hint2: 'Pull together', hint3: '7 letters' },
    ],
    level2: [
      { id: 'phys-11.6.3', type: 'choice', question: 'Where is a magnet\u2019s field STRONGEST?', options: ['Middle', 'Just above', 'At the poles', 'Far away'], answer: 'At the poles', hint1: 'Field lines densest', hint2: 'Edges where lines start/end', hint3: 'Strongest force' },
      { id: 'phys-11.6.4', type: 'choice', question: 'Why does Earth have a magnetic field?', options: ['Atmosphere', 'Spinning iron core', 'Moon\u2019s gravity', 'Solar wind'], answer: 'Spinning iron core', hint1: 'Inside Earth', hint2: 'Iron + motion', hint3: 'Geodynamo' },
    ],
    level3: [
      { id: 'phys-11.6.5', type: 'choice', question: 'Why must field lines never cross?', options: ['Looks ugly', 'A point would have two field directions, which is impossible', 'Random rule', 'They always cross'], answer: 'A point would have two field directions, which is impossible', hint1: 'Field has unique direction', hint2: 'Logical impossibility', hint3: 'Linked to physics rule' },
      { id: 'phys-11.6.6', type: 'text', question: 'Iron filings sprinkled near a magnet line up along the magnetic ___ lines.', answer: 'field', hint1: '5 letters', hint2: 'Same word as topic', hint3: 'Topic essential' },
    ],
  },
};

// =====================================================================
// 11.7  ELECTROMAGNETS
// =====================================================================
const ELECTROMAGNETS_TOPIC = {
  id: 'phys-11.7',
  title: 'Electromagnets',
  slug: 'electromagnets',
  pageRef: 'Section 11 · Topic 7',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Magnetism from current',
        body:
          'A wire carrying an electric current produces a magnetic field around it. Wrap the wire into a coil (a SOLENOID) and the field becomes much stronger and more like a bar magnet\u2019s field. Add a soft IRON CORE down the middle and the field is multiplied many times — that\u2019s an ELECTROMAGNET. As soon as you switch the current OFF, the magnetism vanishes.',
        check: {
          question: 'An electromagnet works because…',
          options: ['Iron always magnetic', 'A current in a coil creates a magnetic field', 'Magic', 'Static charge'],
          answer: 'A current in a coil creates a magnetic field',
          explainer: 'No current = no magnetism. Switch on = field on.',
        },
      },
      {
        heading: 'Strengthening an electromagnet',
        body:
          'Three ways to make an electromagnet stronger: (1) MORE TURNS in the coil; (2) BIGGER current; (3) better core material — using soft iron instead of just air. The more turns and current you have, the more powerful the field. That\u2019s why industrial electromagnets in scrapyards (used to lift cars) need huge currents.',
        check: {
          question: 'Which would NOT make an electromagnet stronger?',
          options: ['More turns of wire', 'Bigger current', 'Removing the iron core', 'Adding more cells'],
          answer: 'Removing the iron core',
          explainer: 'Iron core multiplies the field. Removing it weakens the magnet.',
        },
      },
      {
        heading: 'Where they\u2019re used',
        body:
          'Scrapyard cranes (lift cars then drop them by switching off the current); electric bells (the current pulls a hammer to ring the bell, then breaks its own circuit); maglev trains (electromagnets levitate the train above the track); MRI scanners (a giant electromagnet creates the powerful field used to image patients). The big advantage over a permanent magnet: switchable — you can turn it on and off.',
        check: {
          question: 'A scrapyard crane uses an electromagnet because…',
          options: ['It looks cool', 'Cheap', 'It can be switched off to drop the car', 'No alternative'],
          answer: 'It can be switched off to drop the car',
          explainer: 'Permanent magnet would never let the car go.',
        },
      },
    ],
    keyPoints: [
      'Electric current → magnetic field around the wire',
      'Coil + iron core = electromagnet',
      'Strengthen: more turns, more current, soft iron core',
      'Switchable: ON when current flows, OFF when stopped',
      'Used in scrap cranes, bells, maglev, MRI scanners',
    ],
    workedExamples: [
      {
        problem: 'A simple electromagnet has 50 turns and a current of 0.5 A. Suggest THREE changes that would make it stronger.',
        steps: [
          { step: 1, instruction: 'Increase turns', working: 'e.g. wind 100 turns instead of 50' },
          { step: 2, instruction: 'Increase current', working: 'e.g. add another cell to push more current' },
          { step: 3, instruction: 'Add/improve core', working: 'Use a soft iron core' },
        ],
        answer: 'More turns + bigger current + soft iron core.',
        commonMistake: 'Suggesting "make the wire longer" without saying "more turns" — it\u2019s the turns count and current that matter most.',
      },
    ],
    whyItWorks:
      'An electromagnet is essentially a "magnet on demand". The microscopic spins of electrons in iron line up with the field from the current; multiply that by all the turns of the coil and you get a powerful, controllable magnet. Turn off the current and they all randomise again — so the magnetism vanishes.',
  },
  glossary: [
    { term: 'Solenoid', definition: 'A coil of wire that produces a magnetic field when current flows.' },
    { term: 'Electromagnet', definition: 'A solenoid with a soft iron core — much stronger than the coil alone.' },
    { term: 'Soft iron', definition: 'Iron that magnetises easily but loses its magnetism quickly when current stops.' },
    { term: 'Maglev', definition: 'A high-speed train that floats on a magnetic field generated by electromagnets.' },
  ],
  examQuestion: {
    prompt:
      '(a) Describe how an electromagnet is made. (b) State THREE ways to make an electromagnet stronger. (c) Suggest one major advantage of an electromagnet over a permanent magnet. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'A coil of wire (solenoid) with a soft iron core',
      'Connect the coil to a power supply so a current flows',
      'More turns / bigger current / soft iron core (any 3 from these or more turns plus more current plus closer turns)',
      'Can be switched on and off by controlling the current',
      '(Useful for scrap-yard cranes, bells, MRI etc.)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'phys-11.7.w1', type: 'text', question: 'A coil of wire is called a ___ .', answer: 'solenoid', hint1: '8 letters', hint2: 'Current carries field', hint3: 'Starts with s' },
      { id: 'phys-11.7.w2', type: 'choice', question: 'An electromagnet is strongest with…', options: ['Few turns + small current', 'Many turns + big current + iron core', 'Plastic core', 'No current'], answer: 'Many turns + big current + iron core', hint1: 'All three boosters', hint2: 'Combined approach', hint3: 'Maximises field' },
      { id: 'phys-11.7.w3', type: 'choice', question: 'Switch off current → electromagnet…', options: ['Stays on', 'Loses magnetism', 'Reverses', 'Heats up'], answer: 'Loses magnetism', hint1: 'No current = no field', hint2: 'Switchable', hint3: 'Off' },
    ],
    level1: [
      { id: 'phys-11.7.1', type: 'choice', question: 'Why does a scrapyard crane use an electromagnet rather than a permanent magnet?', options: ['Cheaper', 'Can be switched off to drop the load', 'Looks better', 'More magnetic'], answer: 'Can be switched off to drop the load', hint1: 'Releasing cars', hint2: 'Controllable', hint3: 'On/off advantage' },
      { id: 'phys-11.7.2', type: 'text', question: 'A coil with an iron core is an ___ .', answer: 'electromagnet', hint1: '13 letters', hint2: 'Topic title', hint3: 'Starts with e' },
    ],
    level2: [
      { id: 'phys-11.7.3', type: 'choice', question: 'What kind of iron core works BEST in an electromagnet?', options: ['Steel', 'Soft iron', 'Hard iron', 'Cast iron'], answer: 'Soft iron', hint1: 'Magnetises and demagnetises easily', hint2: 'Two words', hint3: 'Easy to switch' },
      { id: 'phys-11.7.4', type: 'choice', question: 'Increasing the current through an electromagnet will…', options: ['Make it weaker', 'Make it stronger', 'Have no effect', 'Reverse it'], answer: 'Make it stronger', hint1: 'Bigger current = stronger field', hint2: 'Direct relationship', hint3: 'Linear' },
    ],
    level3: [
      { id: 'phys-11.7.5', type: 'choice', question: 'Maglev trains float using…', options: ['Permanent magnets only', 'Electromagnets that repel the track', 'Hydraulics', 'Air pressure'], answer: 'Electromagnets that repel the track', hint1: 'Magnetic levitation', hint2: 'Electromagnetic levitation', hint3: 'No friction' },
      { id: 'phys-11.7.6', type: 'text', question: 'MRI scanners contain a giant ___ to image patients.', answer: 'electromagnet', hint1: '13 letters', hint2: 'Topic title', hint3: 'Starts with e' },
    ],
  },
};

// ---------- assemble the section ----------
const BASE_TOPICS = [
  CIRCUITS_TOPIC,
  MEASURING_TOPIC,
  SERIES_TOPIC,
  PARALLEL_TOPIC,
  STATIC_TOPIC,
  MAGNETS_TOPIC,
  ELECTROMAGNETS_TOPIC,
];

// authoredTopics.json overrides any topic by id (for the PDF pipeline).
export const electricityAndMagnetismTopics = BASE_TOPICS.map((t) =>
  AUTHORED_BY_ID[t.id] ? AUTHORED_BY_ID[t.id] : t,
);
